import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { isAuthPublicPath } from '../utils/authPaths';
import type {
  ApiResponse,
  Pool,
  Wearable,
  Alert,
  WaterQuality,
  SafetyStatus,
  WaterQualityAdjustment,
  SafetyCheck,
  User,
  UserCreate,
  UserUpdate,
  PoolCreate,
  PoolUpdate,
  WearableCreate,
  WearableUpdate,
  AlertCreate,
  AlertUpdate,
  Sensor,
  SensorData,
  MonitoringApi,
  Event,
  MonitoringData,
  PoolSettings,
  PoolSettingsUpdate,
  Administrator,
  AdministratorCreate,
  AdministratorPasswordChange
} from '../types';
import { LoginRequest, LoginResponse, RegisterRequest, ResetPasswordRequest, ResetPasswordConfirmRequest, AdministratorUpdate } from '../types/auth'; // AdministratorUpdate를 auth.ts에서 가져옴
import { Contact, ContactCreate, ContactUpdate, ContactFilter } from '../types/contact';
import { Cctv, CctvCreate, CctvUpdate, CctvFilter } from '../types/cctv';
import { hashSHA256 } from '../utils/hashUtils';
import { poolApi as importedPoolApi } from './poolApi'; // 이름 변경
import { cctvApi as importedCctvApi } from './cctvApi';
import { wearableApi as importedWearableApi } from './wearableApi';
import { poolMonitoringApi as importedMonitoringApi } from './poolMonitoringApi';
import { apiService as importedApiService } from './apiService';
import { message } from 'antd';
import { config } from '../config';
import {
  attachAuthInterceptors,
  handleAuthFailureRetry,
  isCredentialAuthFailure,
} from './authTokenRefresh';
import { getBearerTokenForApi } from '../utils/poolSiteAuth';

// API 요청 캐싱 및 디바운싱을 위한 타입 정의
type CacheItem = {
  data: any;
  timestamp: number;
  expiry: number;
};

type RequestHandler = {
  cancel: () => void;
  promise: Promise<any>;
};

export type ApiGetOptions = {
  useCache?: boolean;
  silentError?: boolean;
};

type RequestConfigWithSilent = AxiosRequestConfig & { silentError?: boolean };

// ApiClient — isCredentialAuthFailure는 authTokenRefresh와 공유
class ApiClient {
  private instance: AxiosInstance;
  private cache: Map<string, CacheItem> = new Map();
  private pendingRequests: Map<string, RequestHandler> = new Map();
  private defaultCacheTTL = 30 * 1000; // 기본 캐시 유효 시간 (30초)
  private maxRetries = 2; // 최대 재시도 횟수
  
  constructor(baseURL: string) {
    // Axios 인스턴스 생성
    this.instance = axios.create({
      baseURL,
      timeout: 30000, // 30초 타임아웃
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    // 요청 인터셉터 설정
    this.instance.interceptors.request.use(
      (config) => {
        const token = getBearerTokenForApi();
        if (token && config.headers) {
          config.headers['Authorization'] = `Bearer ${token}`;
        }
        
        return config;
      },
      (error) => Promise.reject(error)
    );
    
    // 응답 인터셉터 설정
    this.instance.interceptors.response.use(
      (response) => {
        // 요청 키 생성
        const requestKey = this.getRequestKey(response.config);
        
        // 진행 중인 요청에서 제거
        this.pendingRequests.delete(requestKey);
        
        // 캐싱 설정이 있는 경우 캐시 저장
        if (response.config.method?.toLowerCase() === 'get' && 
            response.config.url && 
            !response.config.url.includes('/api/stream/')) {
          const cacheControl = response.headers['cache-control'];
          let ttl = this.defaultCacheTTL;
          
          // 서버 Cache-Control 헤더 분석
          if (cacheControl && cacheControl.includes('max-age=')) {
            const match = cacheControl.match(/max-age=(\d+)/);
            if (match && match[1]) {
              ttl = parseInt(match[1], 10) * 1000; // 초 -> 밀리초 변환
            }
          }
          
          // 캐시에 저장
          this.cache.set(requestKey, {
            data: response.data,
            timestamp: Date.now(),
            expiry: Date.now() + ttl,
          });
        }
        
        return response;
      },
      async (error: any) => {
        // 요청이 취소된 경우
        if (axios.isCancel(error)) {
          return Promise.reject(error);
        }
        
        // 요청 키와 설정
        const config = error.config;
        if (!config) return Promise.reject(error);
        
        const requestKey = this.getRequestKey(config);
        
        // 진행 중인 요청에서 제거
        this.pendingRequests.delete(requestKey);
        
        const cfg = config as RequestConfigWithSilent;

        if (isCredentialAuthFailure(error)) {
          return handleAuthFailureRetry(this.instance, error);
        }
        
        // 재시도 로직
        const retryCount = config.retryCount || 0;
        if (retryCount < this.maxRetries && error.response?.status >= 500) {
          // 서버 오류 시 재시도
          const delay = Math.pow(2, retryCount) * 1000; // 지수 백오프
          
          await new Promise(resolve => setTimeout(resolve, delay));
          
          config.retryCount = retryCount + 1;
          return this.instance(config);
        }
        
        if (!cfg.silentError) {
          this.handleError(error);
        }

        return Promise.reject(error);
      }
    );
    
    // 캐시 정리 타이머 시작
    setInterval(() => this.cleanCache(), 60000); // 1분마다 실행
  }
  
  // 요청 캐싱 키 생성
  private getRequestKey(config: AxiosRequestConfig): string {
    const { url, method, params, data } = config;
    return `${method}:${url}:${params ? JSON.stringify(params) : ''}:${data ? JSON.stringify(data) : ''}`;
  }
  
  // 만료된 캐시 정리
  private cleanCache(): void {
    const now = Date.now();
    for (const [key, item] of this.cache.entries()) {
      if (item.expiry < now) {
        this.cache.delete(key);
      }
    }
  }
  
  // 캐시 완전 비우기
  public clearCache(): void {
    this.cache.clear();
  }

  /** 특정 GET URL(부분 일치) 캐시 제거 — 설정 토글 등 직후 stale 방지 */
  public invalidateGetCache(urlPart: string): void {
    const needle = urlPart.trim();
    if (!needle) return;
    for (const key of [...this.cache.keys()]) {
      if (key.includes(needle)) {
        this.cache.delete(key);
      }
    }
  }
  
  // 오류 처리 및 표시
  private handleError(error: AxiosError): void {
    if (!error.response) {
      // 네트워크 오류 등
      message.error('네트워크 오류 발생');
      return;
    }

    let errorMessage = '오류 발생';
    
    // 오류 메시지 추출 및 문자열로 변환
    if (error.response.data) {
      const data = error.response.data as
        | string
        | { message?: string; error?: string; detail?: string }
        | undefined;
      
      if (typeof data === 'string') {
        errorMessage = data;
      } else if (data?.message && typeof data.message === 'string') {
        errorMessage = data.message;
      } else if (data?.error && typeof data.error === 'string') {
        errorMessage = data.error;
      } else if (data?.detail && typeof data.detail === 'string') {
        errorMessage = data.detail;
      } else {
        // 객체인 경우 직렬화
        errorMessage = '서버 오류: ' + JSON.stringify(data).substring(0, 100);
      }
    }
    
    // 상태 코드별 처리
    switch (error.response.status) {
      case 400:
        message.error(`요청 오류: ${errorMessage}`);
        break;
      case 401:
        // 인증 오류는 인터셉터에서 처리
        break;
      case 403:
        message.error(`접근 권한 없음: ${errorMessage}`);
        break;
      case 404: {
        const url = String(error.config?.url || '');
        // 웨어러블 센서 이력 없음은 정상(빈 값) — 토스트 생략
        if (
          url.includes('/sensor-data/latest') ||
          String(errorMessage).includes('No sensor data found')
        ) {
          break;
        }
        message.error(`요청한 리소스를 찾기 불가: ${errorMessage}`);
        break;
      }
      case 422:
        message.error(`입력 데이터 오류: ${errorMessage}`);
        break;
      case 500:
      case 502:
      case 503:
      case 504:
        message.error(`서버 오류 발생: ${errorMessage}`);
        break;
      default:
        message.error(`오류 발생 (${error.response.status}): ${errorMessage}`);
        break;
    }
  }
  
  // GET 요청 (캐싱 지원)
  public async get<T = any>(
    url: string,
    params?: any,
    options?: boolean | ApiGetOptions,
  ): Promise<T> {
    let useCache = true;
    let silentError = false;
    if (typeof options === 'boolean') {
      useCache = options;
    } else if (options) {
      useCache = options.useCache ?? true;
      silentError = options.silentError ?? false;
    }

    const config: AxiosRequestConfig = { method: 'get', url, params };
    const requestKey = this.getRequestKey(config);

    if (useCache && !url.includes('/api/stream/')) {
      const cachedItem = this.cache.get(requestKey);
      if (cachedItem && cachedItem.expiry > Date.now()) {
        return cachedItem.data;
      }
    }

    const response = await this.instance.get<T>(url, {
      params,
      silentError,
    } as RequestConfigWithSilent);
    return response.data;
  }
  
  // POST 요청
  public async post<T = any>(
    url: string,
    data?: any,
    config?: RequestConfigWithSilent
  ): Promise<T> {
    const response = await this.instance.post<T>(url, data, config);
    return response.data;
  }
  
  // PUT 요청
  public async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.put<T>(url, data, config);
    return response.data;
  }
  
  // DELETE 요청
  public async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.delete<T>(url, config);
    return response.data;
  }
  
  // PATCH 요청
  public async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.patch<T>(url, data, config);
    return response.data;
  }
  
  // 파일 업로드
  public async uploadFile<T = any>(url: string, file: File, onProgress?: (progress: number) => void): Promise<T> {
    const formData = new FormData();
    formData.append('file', file);
    
    const config: AxiosRequestConfig = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };
    
    // 진행률 콜백 설정
    if (onProgress) {
      config.onUploadProgress = (progressEvent) => {
        if (progressEvent.total) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentCompleted);
        }
      };
    }
    
    const response = await this.instance.post<T>(url, formData, config);
    return response.data;
  }
  
  // HTTP 스트리밍 (이벤트 스트림)
  public getEventSource(url: string, onMessage: (data: any) => void, onError?: (error: any) => void): () => void {
    const eventSource = new EventSource(`${this.instance.defaults.baseURL}${url}`);
    
    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        onMessage(data);
      } catch (error) {
        console.error('Event stream parse error:', error);
        if (onError) onError(error);
      }
    };
    
    eventSource.onerror = (error) => {
      console.error('Event stream error:', error);
      if (onError) onError(error);
    };
    
    // 연결 해제 함수 반환
    return () => eventSource.close();
  }
}

// API 클라이언트 생성
export const api = axios.create({
  baseURL: config.apiUrl,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

attachAuthInterceptors(api);

// API 클라이언트 인스턴스 생성
const apiClient = new ApiClient(config.apiUrl);

export function invalidateMonitoringSettingsCache(): void {
  apiClient.invalidateGetCache('/api/settings/system');
  apiClient.invalidateGetCache('/api/central-ai-monitor/status');
}

// 기존 사용처 호환을 위한 named export 유지
export const poolApi = {
  ...importedPoolApi,
  // 레거시 시그니처 호환
  getPool: (poolId: number | string) => importedPoolApi.getPoolById(String(poolId)),
};

export const cctvApi = {
  ...importedCctvApi,
  // 레거시 시그니처 호환
  getCctvs: (poolId: number | string) => importedCctvApi.getCctvsByPoolId(poolId),
  updateCctv: (
    poolId: number | string,
    cctvIdOrData: number | Record<string, unknown>,
    data?: Record<string, unknown>
  ) => {
    if (typeof cctvIdOrData === 'number') {
      return importedCctvApi.updateCctv(poolId, cctvIdOrData, data || {});
    }

    // 구버전 2-인자 호출 호환: updateCctv(poolId, data)
    return api.put(`/api/pools/${poolId}/cctvs`, cctvIdOrData);
  },
};

export const wearableApi = {
  ...importedWearableApi,
  // 레거시 시그니처 호환
  getWearables: (poolId: number | string) => importedWearableApi.getAllWearables(poolId),
  registerWearable: (poolId: number, wearableId: number) =>
    api.post(`/api/pools/${poolId}/wearables/register`, { wearable_id: wearableId }),
  getWearableData: (wearableId: string | number) =>
    api.get(`/api/wearables/${wearableId}/data`),
};

export const monitoringApi = {
  ...importedMonitoringApi,
  getAlerts: async (poolId?: number | string) => {
    const targetPoolId = poolId === undefined || poolId === null ? '' : String(poolId);
    const url = targetPoolId
      ? `/api/monitoring/pools/${targetPoolId}/alerts`
      : '/api/monitoring/alerts';
    const res = await api.get(url);
    return { success: true, data: res.data, message: '요청 성공' };
  },
  getMonitoringData: async (poolCode: string) => {
    const res = await api.get(`/api/monitoring/${poolCode}`);
    return { success: true, data: res.data, message: '요청 성공' };
  },
  getPoolStatus: async (poolId: number | string) => {
    const res = await api.get(`/api/monitoring/pools/${poolId}/status`);
    return { success: true, data: res.data, message: '요청 성공' };
  },
};
export const userApi = {
  getUsers: () => api.get('/api/users').then((r: any) => ({ success: true, data: r.data, message: '요청 성공' })),
  getUser: (id: number) => api.get(`/api/users/${id}`).then((r: any) => ({ success: true, data: r.data, message: '요청 성공' })),
  createUser: (data: any) => api.post('/api/users', data).then((r: any) => ({ success: true, data: r.data, message: '요청 성공' })),
  updateUser: (id: number, data: any) => api.put(`/api/users/${id}`, data).then((r: any) => ({ success: true, data: r.data, message: '요청 성공' })),
  deleteUser: (id: number) => api.delete(`/api/users/${id}`).then((r: any) => ({ success: true, data: r.data, message: '요청 성공' })),
};

export const administratorApi = {
  getAdministrators: () => api.get('/api/administrators').then((r: any) => ({ success: true, data: r.data, message: '요청 성공' })),
  getCurrentAdministrator: () => api.get('/api/administrators/me').then((r: any) => ({ success: true, data: r.data, message: '요청 성공' })),
  getAdministratorById: (id: number) => api.get(`/api/administrators/${id}`).then((r: any) => ({ success: true, data: r.data, message: '요청 성공' })),
  updateAdministrator: (id: number, data: any) => api.put(`/api/administrators/${id}`, data).then((r: any) => ({ success: true, data: r.data, message: '요청 성공' })),
  createAdministrator: (data: any) => api.post('/api/administrators', data).then((r: any) => ({ success: true, data: r.data, message: '요청 성공' })),
  deleteAdministrator: (id: number) => api.delete(`/api/administrators/${id}`).then((r: any) => ({ success: true, data: r.data, message: '요청 성공' })),
  changeAdministratorPassword: (id: number, data: { currentPassword?: string; newPassword: string }) =>
    api.post(`/api/administrators/${id}/password`, data).then((r: any) => ({ success: true, data: r.data, message: '요청 성공' })),
};

export const apiService: any = Object.assign(importedApiService, {
  login: (email: string, password: string) =>
    importedApiService.post('/api/auth/login', { email, password }).then((res: any) => res.data),
  getPools: () =>
    importedApiService.get('/api/pools').then((res: any) => res.data),
  getCctvs: (poolId: number | string) =>
    importedApiService.get(`/api/pools/${poolId}/cctvs`).then((res: any) => res.data),
  createAlert: (poolId: number | string, payload: any) =>
    importedApiService.post(`/api/pools/${poolId}/alerts`, payload).then((res: any) => res.data),
});

export default apiClient;